import { H4 } from "@sellar-io/components";
import { OrderListResponseType } from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { Icon, SpinnerLegacy } from "../../../../components";
import { useDashboardContext } from "../../../../components/dashboard/Dashboard.shared";
import { ErrorMessage } from "../../../../components/errorMessage/ErrorMessage";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import { ToastProvider } from "../../../../components/toast/useToast";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { getOrderDetailsRedirectUrl } from "../AllOrders/AllOrdersList.shared";
import RequestedOrderListElement from "./RequestedOrderListElement";

export const RequestedOrdersList = ({
  query,
  selectedOrderId,
}: {
  query: UseInfiniteQueryResult<InfiniteData<OrderListResponseType>>;
  selectedOrderId: number | null;
}) => {
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();
  const [searchParams] = useSearchParams();
  const isRequestTab =
    searchParams.get("tab") === "Order requests" ||
    searchParams.get("tab") === "Action required";

  const route = getOrderDetailsRedirectUrl({
    orderId: selectedOrderId,
    isTwoPanel,
    query,
  });

  useEffect(() => {
    if (route && isRequestTab && !query.isFetching) {
      //if two panel layout and no orders selected OR if the tab has changed, automatically select first order
      navigate(route, {
        replace: true,
      });
    }
  }, [route, isRequestTab, query.isFetching, navigate]);

  if (query.isPending) return <SpinnerLegacy />;

  if (query.isError)
    return (
      <ErrorMessage>{apiV2AssertError(query.error)?.message}</ErrorMessage>
    );

  if (!query.data.pages[0]?.length) {
    return (
      <div className={"bg-white p-4"}>
        <div
          className={
            "flex h-full flex-col gap-y-2 rounded-lg border bg-zinc-50 p-4 text-zinc-500"
          }
        >
          <Icon icon={"CHECK_CIRCLE"} size={1} className={"text-green-700"} />
          <H4 className={"text-zinc-500"}>
            You're all up to date on your orders
          </H4>
          <p>
            You've accepted all of your order requests. Go to{" "}
            <strong>All orders</strong> for any orders which still need to be
            processed
          </p>
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <InfiniteList query={query} className={"mw-[900px] bg-gray-100"}>
        {query.data.pages.map((page) =>
          page.map((order) => (
            <RequestedOrderListElement
              key={order.id}
              order={order}
              selectedOrderId={selectedOrderId}
            />
          )),
        )}
      </InfiniteList>
    </ToastProvider>
  );
};
